/* eslint-disable react/no-unknown-property */
import React from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Icon, IconButton, Tab } from '@pypestream/design-system';
import { Tenancy_Node_Type_Enum_Ps_Enum } from '@pypestream/api-services';

import {
  NodeColumns,
  convertTextToColumnValue,
  tableStore,
  type TableNode,
  type TableRow,
} from '../../stores/table-store';
import { buildScriptFileNames } from '../../stores/utils';
import { buildPanelCellCommitUpdate } from '../table/expanded-cell-view/panel-cell-updates';
import {
  solutionAssistantStore,
  type AssistantTab,
} from '../../stores/solution-assistant-store';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { SupervisorPanel } from '../table/supervisor/supervisor-panel';
import {
  ActionAssistAborted,
  useActionAssist,
} from '../../hooks/use-action-assist';
import {
  knownVariables,
  summarizeSolution,
} from '../table/node-property-panel/ai-action-assist/action-assist-model';
import {
  assistantContextRowId,
  assistantGreeting,
  assistantHistory,
  assistantStarters,
  assistantWriteGate,
  type AssistantTurn,
  type AssistantSettingChange,
} from './solution-assistant-model';
import {
  PROPOSAL_COLUMN_LABELS,
  buildProposalUpdates,
  currentProposalText,
  defaultKeep,
  findAgentByName,
  validateNodeProposals,
  type NodeProposal,
  type NodeProposalDraft,
  type ProposalColumn,
  type ProposalRow,
} from './node-proposals';

import '../table/node-property-panel/ai-action-assist/ai-action-assist-panel.scss';
import './solution-assistant-dock.scss';
import { RichAnswer } from './rich-answer';
import { BobcatTabGroup } from '../bobcat-tab-group';
import { AssistantAskCard, SettingChangeCard } from './setting-change-card';
import { useAgentSettings } from '../../pages/agent-settings/use-agent-settings';
import { agentSettingsStore } from '../../stores/agent-settings-store';
import { AgentSettingsPanel } from '../../pages/agent-settings/agent-settings-panel';

/** `{NAME}` and `#1234` inside a message, drawn as the pills they are. */
const RichText: React.FC<{ text: string }> = ({ text }) => {
  const parts = text.split(/(\{[A-Z][A-Z0-9_]*\}|#\d{1,6}\b)/g);
  return (
    <>
      {parts.map((part, i) => {
        if (/^\{[A-Z][A-Z0-9_]*\}$/.test(part)) {
          return (
            <span key={i} className="c-ai-assist__ref c-ai-assist__ref--var">
              {part.slice(1, -1)}
            </span>
          );
        }
        if (/^#\d{1,6}$/.test(part)) {
          return (
            <span key={i} className="c-ai-assist__ref c-ai-assist__ref--node">
              {part.slice(1)}
            </span>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
};

/** Columns stored as something other than panel text, converted the way
 *  the grid's own commit converts them. */
const commitProposalField = (
  rowId: string,
  column: ProposalColumn,
  text: string
): Partial<TableRow> => {
  if (column === 'behaviors' || column === 'answerRequired') {
    return {
      [column]: convertTextToColumnValue(text, column as keyof TableNode),
    } as Partial<TableRow>;
  }
  return buildPanelCellCommitUpdate(
    column,
    text,
    tableStore.isJsonColumn(rowId, column as keyof TableNode)
  );
};

/**
 * One node's proposed content: a card per field (new, or replacing what the
 * node has), each kept or dropped, and Apply — which writes only the kept
 * fields, as ONE undoable edit through the grid's own write. Nothing is
 * written before Apply.
 */
const ProposalCard: React.FC<{
  proposal: NodeProposal;
  applied: boolean;
  onApplied: () => void;
}> = observer(({ proposal, applied, onApplied }) => {
  const [keep, setKeep] = React.useState(() => defaultKeep(proposal));
  // Once Apply has created a proposed new node, its real row id and number.
  const [createdRowId, setCreatedRowId] = React.useState<string | null>(null);
  const rowId = createdRowId ?? proposal.rowId;
  const row = tableStore.getRowById(rowId);
  const kept = proposal.fields.filter((f) => keep[f.column] !== false);
  const gate = assistantWriteGate(tableStore);
  const lockedHintId = React.useId();
  const readOnly = gate === 'view-only';
  const isNew = !!proposal.create;

  /**
   * A proposed NEW node is created here, at Apply, never before: into the
   * agent it names — found by name NOW, so a second new node for an agent
   * the first card just created lands beside it rather than in a duplicate —
   * or into a new agent of that name.
   */
  const createProposedNode = (): string | null => {
    const target = proposal.create;
    if (!target) return null;
    const nodeType =
      proposal.kind === 'action'
        ? Tenancy_Node_Type_Enum_Ps_Enum.A
        : Tenancy_Node_Type_Enum_Ps_Enum.D;
    const agent = findAgentByName(
      tableStore.getAllSortedRows() as unknown as ProposalRow[],
      target.agent
    );
    if (agent) {
      return tableStore.addRowToGroup(agent.id, { nodeType }) || null;
    }
    return (
      tableStore.createCanvasAgent(target.agent, { nodeType })?.rowId ?? null
    );
  };

  const apply = () => {
    if (applied || gate !== 'open') return;
    if (!isNew && (!row || !kept.length)) return;
    const targetRowId = isNew ? createProposedNode() : proposal.rowId;
    if (!targetRowId) return;
    if (isNew) setCreatedRowId(targetRowId);
    if (kept.length) {
      const updates = buildProposalUpdates(proposal, keep, (column, text) =>
        commitProposalField(targetRowId, column, text)
      );
      tableStore.recordRowEdit(
        targetRowId,
        `Apply AI proposal to node ${
          tableStore.getRowById(targetRowId)?.nodeNumber ?? proposal.nodeNumber
        }`,
        () => {
          tableStore.updateRow({
            rowId: targetRowId,
            updates,
            skipValidationRules: false,
            saveImmediately: true,
          });
        },
        kept[0].column as NodeColumns
      );
    }
    onApplied();
  };

  return (
    <div
      className="c-solution-assistant__proposal"
      test-id="ps-solution-assistant-proposal"
    >
      <div className="c-solution-assistant__proposal-head">
        <span className="c-ai-assist__ref c-ai-assist__ref--node">
          {isNew ? (row?.nodeNumber ?? 'New') : proposal.nodeNumber}
        </span>
        <span className="c-solution-assistant__proposal-name">
          {proposal.nodeName}
        </span>
        <span className="c-solution-assistant__proposal-kind">
          {proposal.kind === 'decision' ? 'Decision node' : 'Action node'}
        </span>
      </div>
      {isNew && proposal.create && (
        <span
          className="c-solution-assistant__proposal-note"
          test-id="ps-solution-assistant-proposal-new"
        >
          {proposal.create.agentId
            ? `New node in ${proposal.create.agent}.`
            : `New agent ${proposal.create.agent}, starting with this node.`}{' '}
          Created when you apply.
        </span>
      )}
      {!row && !isNew && (
        <span className="c-solution-assistant__proposal-note">
          This node is no longer in the solution.
        </span>
      )}
      {proposal.fields.map((f) => {
        const on = keep[f.column] !== false;
        const current = row
          ? currentProposalText(
              row as unknown as Record<string, unknown>,
              f.column
            )
          : '';
        return (
          <div
            key={f.column}
            className={`c-solution-assistant__field${on ? '' : ' c-solution-assistant__field--off'}`}
            test-id={`ps-solution-assistant-field-${f.column}`}
          >
            <div className="c-solution-assistant__field-head">
              <span className="c-solution-assistant__field-label">
                {PROPOSAL_COLUMN_LABELS[f.column]}
              </span>
              <span className="c-solution-assistant__field-tag">
                {current.trim() ? 'Replaces current' : 'New'}
              </span>
              {!applied && !readOnly && (
                <button
                  type="button"
                  className="c-ai-assist__act"
                  aria-pressed={on}
                  onClick={() => setKeep({ ...keep, [f.column]: !on })}
                >
                  {on ? 'Drop' : 'Keep'}
                </button>
              )}
            </div>
            <pre className="c-solution-assistant__field-value">{f.value}</pre>
            {f.why && (
              <span className="c-solution-assistant__field-why">{f.why}</span>
            )}
          </div>
        );
      })}
      {proposal.notes.map((n) => (
        <span key={n} className="c-solution-assistant__proposal-note">
          {n}
        </span>
      ))}
      <div className="c-solution-assistant__proposal-actions">
        {applied ? (
          <span
            className="c-solution-assistant__applied"
            test-id="ps-solution-assistant-applied"
          >
            {isNew
              ? `Created node ${row?.nodeNumber ?? ''} in ${proposal.create?.agent}`
              : `Applied to node ${proposal.nodeNumber} — undo with ⌘Z`}
          </span>
        ) : readOnly ? (
          <span className="c-solution-assistant__proposal-note">
            View only — you can&apos;t change this solution.
          </span>
        ) : (
          <button
            type="button"
            className="c-solution-assistant__apply"
            test-id="ps-solution-assistant-apply"
            // A new node has no row until Apply creates it, and is worth
            // creating even with every field dropped. PSTUDIO-4632: never
            // while the table is locked (a publish) — the write unlocks it.
            disabled={
              gate === 'locked' || (isNew ? false : !row || !kept.length)
            }
            aria-describedby={gate === 'locked' ? lockedHintId : undefined}
            onClick={apply}
          >
            {isNew && proposal.create
              ? proposal.create.agentId
                ? `Create node in ${proposal.create.agent}`
                : `Create ${proposal.create.agent} agent and node`
              : `Apply ${kept.length} field${kept.length === 1 ? '' : 's'} to node ${proposal.nodeNumber}`}
          </button>
        )}
        {/* PSTUDIO-4632: said in text, not only a disabled button's title,
            which keyboard and screen-reader users never reach. */}
        {!applied && gate === 'locked' && (
          <span
            id={lockedHintId}
            className="c-solution-assistant__proposal-note"
          >
            Editing is paused while the solution publishes.
          </span>
        )}
      </div>
    </div>
  );
});

/**
 * The dock's "Ask AI" tab. Answers come from Claude, grounded in THIS
 * solution's rows (`summarizeSolution`) — about the whole solution, or about
 * the selected node when there is one.
 */
const AskAiPane: React.FC = observer(() => {
  const { ask, stop } = useActionAssist();
  const draftFiles = useProStudioCtxSelector((ctx) => ctx.draft?.files ?? []);
  const { actionNodeNames } = tableStore;
  const commands = React.useMemo(
    () => buildScriptFileNames(draftFiles, actionNodeNames),
    [draftFiles, actionNodeNames]
  );
  const solutionKey = tableStore.solutionVersionId;
  const rows = tableStore.getAllSortedRows();

  const contextRowId = assistantContextRowId(
    tableStore.selectedNodeRowId,
    solutionAssistantStore.clearedRowId
  );
  const contextRow = contextRowId
    ? tableStore.getRowById(contextRowId)
    : undefined;
  const nodeNumber =
    contextRow?.nodeNumber != null ? String(contextRow.nodeNumber) : null;

  const turns = solutionAssistantStore.turnsFor(solutionKey);
  // Every write reads the transcript at write time: `say` answers after an
  // await, and a card's Apply can land while a question is in flight.
  const appendTurn = (turn: AssistantTurn) =>
    solutionAssistantStore.appendTurn(solutionKey, turn);

  const [busy, setBusy] = React.useState(false);
  const [text, setText] = React.useState('');
  const agentSettings = useAgentSettings();
  const [refPick, setRefPick] = React.useState<null | 'var' | 'node' | 'all'>(
    null
  );
  const [refQuery, setRefQuery] = React.useState('');
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // Once per solution, not per keystroke: a 500-node solution makes this real work.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const known = React.useMemo(() => knownVariables(rows), [solutionKey]);

  React.useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [turns.length, busy]);

  React.useEffect(() => stop, [stop]);

  const say = async (question: string) => {
    const q = question.trim();
    if (!q || busy) return;
    const history = assistantHistory(turns);
    appendTurn({ who: 'me', text: q });
    setText('');
    setRefPick(null);
    setBusy(true);
    try {
      const result = await ask({
        question: q,
        context: summarizeSolution(
          tableStore.getAllSortedRows().map((r) => toJS(r)),
          contextRowId
        ),
        ...(nodeNumber ? { nodeNumber } : {}),
        history,
        commands,
        ...(agentSettings.loaded
          ? {
              agentSettings: {
                supervisor: { ...agentSettings.supervisor },
                knowledge: { ...agentSettings.knowledge },
              },
            }
          : {}),
      });
      // Each proposed setting with the value it replaces; one already at its
      // proposed value is not a change.
      const settingChanges: AssistantSettingChange[] = (
        result.settingChanges ?? []
      )
        .map((c) => ({
          ...c,
          from: (
            agentSettings[c.agent] as unknown as Record<
              string,
              string | boolean
            >
          )[c.key],
        }))
        .filter((c) => c.from !== undefined && c.from !== c.to);
      const { proposals, notes } = validateNodeProposals(
        (result.proposals ?? []) as NodeProposalDraft[],
        tableStore.getAllSortedRows(),
        { commands }
      );
      appendTurn({
        who: 'ai',
        text: result.answer,
        ...(proposals.length ? { proposals } : {}),
        ...(notes.length ? { notes } : {}),
        ...(settingChanges.length ? { settingChanges } : {}),
        ...(result.ask && !settingChanges.length ? { ask: result.ask } : {}),
      });
    } catch (err) {
      if (!(err instanceof ActionAssistAborted)) {
        appendTurn({
          who: 'ai',
          text: `Couldn't answer: ${err instanceof Error ? err.message : String(err)}`,
          error: true,
        });
      }
    } finally {
      setBusy(false);
    }
  };

  // ---- agent-setting changes ----
  const [settingsBusy, setSettingsBusy] = React.useState(false);
  const updateTurn = (i: number, patch: Partial<AssistantTurn>) =>
    solutionAssistantStore.updateTurn(solutionKey, i, (turn) => ({
      ...turn,
      ...patch,
    }));
  /** Saves the ticked changes (`to`), or puts them back (`from`). */
  const writeSettings = async (
    changes: AssistantSettingChange[],
    side: 'to' | 'from'
  ) => {
    const on = changes.filter((c) => !c.off);
    const next = {
      supervisor: { ...agentSettings.supervisor },
      knowledge: { ...agentSettings.knowledge },
    };
    on.forEach((c) => {
      (next[c.agent] as unknown as Record<string, string | boolean>)[c.key] =
        c[side];
    });
    const agents = new Set(on.map((c) => c.agent));
    setSettingsBusy(true);
    const ok = await agentSettings.save({
      ...(agents.has('supervisor') ? { supervisor: next.supervisor } : {}),
      ...(agents.has('knowledge') ? { knowledge: next.knowledge } : {}),
    });
    setSettingsBusy(false);
    if (ok && on[0]) {
      // Show where it landed: the Agents tab beside this chat, the changed
      // settings lit.
      agentSettingsStore.show(
        on[0].agent,
        on.filter((c) => c.agent === on[0].agent).map((c) => c.key)
      );
      solutionAssistantStore.setTab('agents');
    }
    return ok;
  };

  // ---- references: + Variable, + Node, or @ ----
  const nodeRefs = rows
    .filter((r) => r.nodeNumber != null && String(r.nodeType) !== 'g')
    .map((r) => ({
      kind: 'node' as const,
      token: `#${r.nodeNumber}`,
      label: `${r.nodeNumber} ${String(r.nodeName ?? '')}`,
    }));
  const varRefs = known.map((v) => ({
    kind: 'var' as const,
    token: `{${v}}`,
    label: v,
  }));
  const refOptions = (
    refPick === 'node'
      ? nodeRefs
      : refPick === 'var'
        ? varRefs
        : [...varRefs, ...nodeRefs]
  )
    .filter((o) =>
      refQuery ? o.label.toLowerCase().includes(refQuery.toLowerCase()) : true
    )
    .slice(0, 8);

  const insertRef = (token: string) => {
    const el = inputRef.current;
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const at = before.match(/@([^\s@]*)$/);
    const start = at ? caret - at[0].length : caret;
    setText(`${text.slice(0, start)}${token} ${text.slice(caret)}`);
    setRefPick(null);
    setRefQuery('');
    requestAnimationFrame(() => el?.focus());
  };

  const onInput = (value: string, caret: number) => {
    setText(value);
    const at = value.slice(0, caret).match(/@([^\s@]*)$/);
    if (at) {
      setRefPick('all');
      setRefQuery(at[1]);
    } else if (refPick === 'all') {
      setRefPick(null);
    }
  };

  return (
    <>
      {/* Only when a question is about one node; otherwise it's the whole
          solution and there's nothing to say. */}
      {nodeNumber && contextRowId && (
        <div
          className="c-solution-assistant__context"
          test-id="ps-solution-assistant-context"
        >
          <span
            className="c-solution-assistant__about"
            test-id="ps-solution-assistant-about"
          >
            About node {nodeNumber}
            <button
              type="button"
              className="c-solution-assistant__about-x"
              aria-label={`Ask about the whole solution, not node ${nodeNumber}`}
              onClick={() => solutionAssistantStore.clearNode(contextRowId)}
            >
              ×
            </button>
          </span>
        </div>
      )}

      <div
        className="c-ai-assist__body c-ai-assist__chat-body"
        ref={bodyRef}
        test-id="ps-solution-assistant-chat"
      >
        <div className="c-ai-assist__chat-turn c-ai-assist__chat-turn--ai">
          <span className="c-ai-assist__chat-av">
            <Icon name="stars" size="xsmall" />
          </span>
          <div className="c-ai-assist__chat-msg">
            {assistantGreeting(nodeNumber)}
          </div>
        </div>
        {turns.map((t, i) => (
          <div
            key={i}
            className={`c-ai-assist__chat-turn c-ai-assist__chat-turn--${t.who}`}
          >
            {t.who === 'ai' && (
              <span className="c-ai-assist__chat-av">
                <Icon name="stars" size="xsmall" />
              </span>
            )}
            <div
              className={`c-ai-assist__chat-msg${
                t.error ? ' c-ai-assist__chat-msg--error' : ''
              }`}
              test-id={
                t.who === 'ai' ? 'ps-solution-assistant-answer' : undefined
              }
            >
              {t.who === 'ai' && !t.error ? (
                <RichAnswer text={t.text} />
              ) : (
                <RichText text={t.text} />
              )}
              {t.notes?.map((n) => (
                <span key={n} className="c-solution-assistant__hint">
                  {n}
                </span>
              ))}
              {t.proposals?.map((p) => (
                <ProposalCard
                  key={p.rowId}
                  proposal={p}
                  applied={!!t.appliedRowIds?.includes(p.rowId)}
                  onApplied={() =>
                    solutionAssistantStore.updateTurn(
                      solutionKey,
                      i,
                      (turn) => ({
                        ...turn,
                        appliedRowIds: [...(turn.appliedRowIds ?? []), p.rowId],
                      })
                    )
                  }
                />
              ))}
              {t.settingChanges && (
                <SettingChangeCard
                  changes={t.settingChanges}
                  status={t.settingsStatus}
                  busy={settingsBusy}
                  readOnly={tableStore.isViewer}
                  locked={assistantWriteGate(tableStore) === 'locked'}
                  onToggle={(c) =>
                    updateTurn(i, {
                      settingChanges: t.settingChanges?.map((x, k) =>
                        k === c ? { ...x, off: !x.off } : x
                      ),
                    })
                  }
                  onApply={async () => {
                    // PSTUDIO-4632: the button is disabled too; this holds if
                    // the lock lands between render and click.
                    if (assistantWriteGate(tableStore) !== 'open') return;
                    if (await writeSettings(t.settingChanges ?? [], 'to')) {
                      updateTurn(i, { settingsStatus: 'applied' });
                    }
                  }}
                  onDiscard={() =>
                    updateTurn(i, { settingsStatus: 'discarded' })
                  }
                  onUndo={async () => {
                    if (assistantWriteGate(tableStore) !== 'open') return;
                    if (await writeSettings(t.settingChanges ?? [], 'from')) {
                      updateTurn(i, { settingsStatus: undefined });
                    }
                  }}
                />
              )}
              {t.ask && i === turns.length - 1 && (
                <AssistantAskCard
                  question={t.ask.question}
                  why={t.ask.why}
                  options={t.ask.options}
                  disabled={busy}
                  onPick={(option) => void say(option)}
                />
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="c-ai-assist__chat-turn c-ai-assist__chat-turn--ai">
            <span className="c-ai-assist__chat-av">
              <Icon name="stars" size="xsmall" />
            </span>
            <div className="c-ai-assist__chat-msg c-ai-assist__chat-msg--thinking">
              Reading the solution…
            </div>
          </div>
        )}
        {turns.length === 0 && !busy && (
          <div className="c-ai-assist__chip-row">
            {assistantStarters(nodeNumber).map((c) => (
              <button
                key={c}
                type="button"
                className="c-ai-assist__chip"
                onClick={() => void say(c)}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="c-ai-assist__refine">
        <div className="c-ai-assist__ref-bar">
          <button
            type="button"
            className="c-ai-assist__act"
            onClick={() => {
              setRefQuery('');
              setRefPick(refPick === 'var' ? null : 'var');
            }}
          >
            + Variable
          </button>
          <button
            type="button"
            className="c-ai-assist__act"
            onClick={() => {
              setRefQuery('');
              setRefPick(refPick === 'node' ? null : 'node');
            }}
          >
            + Node
          </button>
          <span className="c-ai-assist__ref-hint">
            or type <b>@</b>
          </span>
        </div>
        {refPick && (
          <div className="c-ai-assist__ref-menu" role="listbox">
            {refPick !== 'all' && (
              <input
                autoFocus
                className="c-ai-assist__input"
                value={refQuery}
                placeholder={
                  refPick === 'var' ? 'Find a variable…' : 'Find a node…'
                }
                aria-label="Find a reference"
                onChange={(e) => setRefQuery(e.target.value)}
              />
            )}
            {refOptions.length === 0 ? (
              <span className="c-ai-assist__ref-empty">Nothing matches.</span>
            ) : (
              refOptions.map((o) => (
                <button
                  key={`${o.kind}:${o.token}`}
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="c-ai-assist__ref-option"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertRef(o.token);
                  }}
                >
                  <span
                    className={`c-ai-assist__ref-kind c-ai-assist__ref-kind--${o.kind}`}
                  >
                    {o.kind === 'var' ? 'var' : 'node'}
                  </span>
                  <span className="c-ai-assist__ref-label">{o.label}</span>
                </button>
              ))
            )}
          </div>
        )}
        <div className="c-ai-assist__refine-box">
          <textarea
            ref={inputRef}
            rows={1}
            value={text}
            placeholder="Ask, or ask me to fill in a node…"
            aria-label="Ask about this solution"
            test-id="ps-solution-assistant-input"
            onChange={(e) =>
              onInput(
                e.target.value,
                e.target.selectionStart ?? e.target.value.length
              )
            }
            onKeyDown={(e) => {
              if (
                refPick === 'all' &&
                (e.key === 'Enter' || e.key === 'Tab') &&
                refOptions[0]
              ) {
                e.preventDefault();
                insertRef(refOptions[0].token);
                return;
              }
              if (e.key === 'Escape') setRefPick(null);
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void say(text);
              }
            }}
          />
          <button
            type="button"
            className="c-ai-assist__send"
            aria-label="Send"
            test-id="ps-solution-assistant-send"
            disabled={busy || !text.trim()}
            onClick={() => void say(text)}
          >
            ➤
          </button>
        </div>
      </div>
    </>
  );
});

/**
 * The Agents tab: Agent settings — what the Supervisor and Knowledge Agent
 * do, in plain choices — with the full configuration (prompts, model,
 * knowledge files) one click down under Advanced. A caller that opens a
 * specific configuration tab (`openAgents('knowledge-agent')`) lands there.
 */
const AgentsPane: React.FC<{ hasAdvanced: boolean }> = observer(
  ({ hasAdvanced }) => {
    const panelTab = solutionAssistantStore.agentsPanelTab;
    const [advanced, setAdvanced] = React.useState(!!panelTab);
    React.useEffect(() => {
      if (panelTab) setAdvanced(true);
    }, [panelTab]);

    return (
      <div
        className="c-solution-assistant__agents"
        test-id="ps-solution-assistant-agents"
      >
        {advanced && hasAdvanced ? (
          <>
            <button
              type="button"
              className="c-solution-assistant__agents-back"
              onClick={() => setAdvanced(false)}
            >
              <Icon name="chevron-left" size="xsmall" />
              Agent settings
            </button>
            <SupervisorPanel
              embedded
              initialTab={panelTab ?? undefined}
              viewOnly={tableStore.isViewer}
              onClose={() => solutionAssistantStore.close()}
            />
          </>
        ) : (
          <>
            <AgentSettingsPanel embedded />
            {hasAdvanced && (
              <button
                type="button"
                className="c-solution-assistant__agents-advanced"
                test-id="ps-solution-assistant-agents-advanced"
                onClick={() => setAdvanced(true)}
              >
                Advanced configuration: prompts, model, knowledge files
                <Icon name="chevron-right" size="xsmall" />
              </button>
            )}
          </>
        )}
      </div>
    );
  }
);

const TABS: { id: AssistantTab; label: string }[] = [
  { id: 'ask', label: 'Ask AI' },
  { id: 'agents', label: 'Agents' },
];

/**
 * Project Bobcat — the header's AI dock (behind the master `bobcat-five-stage-nav`), a
 * right-side panel on every stage of the Workflows page. "Ask AI" is the
 * solution chat; "Agents" is the real Supervisor / Knowledge Agent settings
 * panel (`SupervisorPanel`), which used to open only from Table View's
 * toolbar and now lives here so it is reachable from every stage.
 */
export const SolutionAssistantDock: React.FC = observer(() => {
  // Same test Table View's toolbar used for showing "Configure Agents".
  const hasAgents = !!useProStudioCtxSelector(
    (ctx) => ctx.draftVersionSolutionData?.llmCredential?.llmProviderId
  );
  const { tab } = solutionAssistantStore;

  return (
    <aside
      className={`c-ai-assist c-solution-assistant c-solution-assistant--${tab}`}
      aria-label="AI and agents"
      test-id="ps-solution-assistant"
    >
      <div className="c-ai-assist__head c-solution-assistant__head">
        <Icon
          name="stars"
          size="small"
          className="c-solution-assistant__icon"
        />
        <BobcatTabGroup
          variant="flush"
          className="c-solution-assistant__tabs"
          test-id="ps-solution-assistant-tabs"
          onTabShow={(e: CustomEvent<{ name: string }>) =>
            solutionAssistantStore.setTab(
              e.detail.name === 'agents' ? 'agents' : 'ask'
            )
          }
        >
          {TABS.map(({ id, label }) => (
            <Tab
              key={id}
              slot="tab"
              panel={id}
              active={tab === id}
              test-id={`ps-solution-assistant-tab-${id}`}
            >
              {label}
            </Tab>
          ))}
        </BobcatTabGroup>
        <IconButton
          name="close"
          size="small"
          test-id="ps-solution-assistant-close"
          aria-label="Close"
          onClick={() => solutionAssistantStore.close()}
        />
      </div>

      {tab === 'ask' ? <AskAiPane /> : <AgentsPane hasAdvanced={hasAgents} />}
    </aside>
  );
});
